import { ComponentChild } from 'preact'
import { PureComponent } from 'preact-compat'
import {
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_CONVERSATION,
  CONTENT_TYPE_IDEA,
  CONTENT_TYPE_QUESTION,
  ContentType
} from '@insided/shared/utils/contentType'
import ChangeAuthorModal from './ChangeAuthorModal'
import { ToggleTrashModal, ToggleTrash } from './ToggleTrash'
import { Author, ContentTypeConfig, ParentCategory } from '../interfaces'
import ConvertTopicModal from './ConvertTopicModal'
import { markAsUnreadByTeam } from '../services/topicService'

/* eslint-disable import/no-internal-modules */
import { ModulesContext } from '@insided/deprecated-utils/globals'
import DropdownControl from '@insided/deprecated-preact-components/dropdown/DropdownControl'
import Button from '@insided/control/uikit/Button'
import { ConfigContext } from '../utils'
import CopyTopicModal from '@insided/control/modules/ContentModeration/TopicModeration/TopicHeader/CopyTopicModal'
import { Module } from '@insided/deprecated-services/modules'

interface MoreOptionsProps {
  topicId: number
  publicId: number
  contentType: ContentType
  trashed: boolean
  reported: boolean
  spam: boolean
  handleToggleSpamModal: () => void
  onTrash: (trashed: boolean) => void
  onShowAlert: (type: 'success' | 'error', message?: string) => void
  categories: ParentCategory[]
  setMessage: (message?: ComponentChild) => void
  hasPinnedReply: boolean
  onChangeAuthor: (author: Author) => void
}

interface MoreOptionsState {
  showChangeAuthorModal: boolean
  showCopyTopicModal: boolean
  showConversionModal: boolean
  showToggleTrashModal: boolean
  convertToContentType: ContentType | null
}

class MoreOptions extends PureComponent<MoreOptionsProps, MoreOptionsState> {
  constructor() {
    super()
    this.state = {
      showChangeAuthorModal: false,
      showCopyTopicModal: false,
      showConversionModal: false,
      showToggleTrashModal: false,
      convertToContentType: null
    }

    this.showConversionModal = this.showConversionModal.bind(this)
    this.hideConversionModal = this.hideConversionModal.bind(this)
    this.markUnreadByTeam = this.markUnreadByTeam.bind(this)
  }

  showChangeAuthorModal(show: boolean) {
    this.setState({ showChangeAuthorModal: show })
  }

  showCopyTopicModal(show: boolean) {
    this.setState({ showCopyTopicModal: show })
  }

  showToggleTrashModal(show: boolean) {
    this.setState({ showToggleTrashModal: show })
  }

  showConversionModal(contentType: ContentType) {
    const { hasPinnedReply, onShowAlert } = this.props
    if (hasPinnedReply) {
      onShowAlert(
        'error',
        "It's not possible to convert a topic with a pinned reply. Please first unpin the reply and try again."
      )
      return
    }
    this.setState({
      showConversionModal: true,
      convertToContentType: contentType
    })
  }

  hideConversionModal() {
    this.setState({
      showConversionModal: false,
      convertToContentType: null
    })
  }

  async markUnreadByTeam() {
    const { contentType, topicId, onShowAlert } = this.props
    try {
      await markAsUnreadByTeam(contentType, topicId)
      window.location.assign('/overview')
    } catch (e) {
      onShowAlert('error')
    }
  }

  getMarkAsUnreadByTeamAction() {
    return {
      title: 'Mark as unread by team',
      onClick: async () => await this.markUnreadByTeam(),
      variant: 'primary'
    }
  }

  getChangeAuthorAction() {
    return {
      title: 'Change author',
      onClick: () => this.showChangeAuthorModal(true),
      variant: 'primary'
    }
  }

  getCopyTopicAction() {
    return {
      title: 'Copy topic',
      onClick: () => this.showCopyTopicModal(true),
      variant: 'primary'
    }
  }

  getConvertAction(contentType: ContentType) {
    return {
      title: `Convert to ${contentType}`,
      onClick: () => this.showConversionModal(contentType),
      variant: 'primary'
    }
  }

  getSpamAction() {
    const { spam, handleToggleSpamModal } = this.props
    return {
      title: spam ? 'Mark as not spam' : 'Mark as spam',
      onClick: handleToggleSpamModal,
      variant: spam ? 'primary' : 'danger'
    }
  }

  getTrashAction() {
    const { trashed } = this.props
    return {
      title: trashed ? 'Restore' : 'Trash',
      onClick: trashed ? async () => await ToggleTrash(this.props) : () => this.showToggleTrashModal(true),
      variant: trashed ? 'primary' : 'danger'
    }
  }

  getDropdownItems(config: ContentTypeConfig, modules: Module[]) {
    const { reported, trashed, spam } = this.props
    const dropdownItems = []

    dropdownItems.push(this.getMarkAsUnreadByTeamAction())
    if ((modules.includes('changeAuthorOfPublishedContent') && config.changeAuthor)) {
      dropdownItems.push(this.getChangeAuthorAction())
    }
    if (config.copyTopic) {
      dropdownItems.push(this.getCopyTopicAction())
    }
    if (!reported) {
      if (config.convertToQuestion) {
        dropdownItems.push(this.getConvertAction(CONTENT_TYPE_QUESTION))
      }
      if (config.convertToConversation) {
        dropdownItems.push(this.getConvertAction(CONTENT_TYPE_CONVERSATION))
      }
      if (config.convertToArticle) {
        dropdownItems.push(this.getConvertAction(CONTENT_TYPE_ARTICLE))
      }
      if (modules.includes('ideationV2') && config.convertToIdea) {
        dropdownItems.push(this.getConvertAction(CONTENT_TYPE_IDEA))
      }
    }
    if (config.spam && !trashed) {
      dropdownItems.push(this.getSpamAction())
    }
    if (!spam) {
      dropdownItems.push(this.getTrashAction())
    }

    return dropdownItems
  }

  render() {
    const {
      topicId,
      publicId,
      contentType,
      trashed,
      onTrash,
      onShowAlert,
      categories,
      setMessage,
      onChangeAuthor
    } = this.props
    const {
      showConversionModal,
      showChangeAuthorModal,
      showCopyTopicModal,
      showToggleTrashModal,
      convertToContentType
    } = this.state

    return (
      <div>
        <ModulesContext.Consumer>
          {(modules) => {
            return (
              <ConfigContext.Consumer>
                {({ contentTypeConfig }) => {
                  return (
                    <DropdownControl
                      align='adjust'
                      triggerClass='dropdown-wrapper'
                      trigger={
                        <Button icon='arrowDropdown' title='More options' data-qa='qa-topic-moderation-more-options' />
                      }
                      items={this.getDropdownItems(contentTypeConfig, modules)}
                    />
                  )
                }}
              </ConfigContext.Consumer>
            )
          }}
        </ModulesContext.Consumer>
        {showChangeAuthorModal && (
          <ChangeAuthorModal
            contentType={contentType}
            topicId={topicId}
            onChangeAuthor={onChangeAuthor}
            onClose={() => this.showChangeAuthorModal(false)}
            onShowAlert={onShowAlert}
          />
        )}
        {showCopyTopicModal && (
          <CopyTopicModal
            topicId={topicId}
            contentType={contentType}
            categories={categories}
            onClose={() => this.showCopyTopicModal(false)}
            onShowAlert={onShowAlert}
          />
        )}
        {showConversionModal && convertToContentType && (
          <ConvertTopicModal
            topicId={topicId}
            publicId={publicId}
            fromContentType={contentType}
            toContentType={convertToContentType}
            categories={categories}
            onShowAlert={onShowAlert}
            hideModal={this.hideConversionModal}
            setMessage={setMessage}
          />
        )}
        {showToggleTrashModal && (
          <ToggleTrashModal
            contentType={contentType}
            topicId={topicId}
            trashed={trashed}
            onTrash={onTrash}
            onClose={() => this.showToggleTrashModal(false)}
            onShowAlert={onShowAlert}
          />
        )}
      </div>
    )
  }
}

export default MoreOptions
